/**
 * @fileoverview Universal logging system for TechStack frontend
 * @module lib/logger
 *
 * USAGE:
 * import logger from './lib/logger';
 * logger.api.info('Request started', { endpoint: '/users' });
 * logger.auth.error('Login failed', { email }, error);
 *
 * DEPENDENCIES:
 * - None (standalone utility)
 */

const LOG_LEVELS = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  FATAL: 5,
  NONE: 6
};

/**
 * Default configuration for the logger
 * @type {Object}
 */
const config = {
  level: process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG,
  enableConsole: true,
  enableStorage: process.env.NODE_ENV !== 'production',
  maxStoredLogs: 1000,
  enableTimestamps: true,
  enableCorrelationIds: true,
  contexts: {
    API: true,
    AUTH: true,
    COMPONENT: true,
    STATE: true,
    UI: true,
    ERROR: true,
    PERF: true,
    SANKEY: true,
    ZEROTRUST: true,
    PROCUREMENT: true,
    VENDOR_ANALYSIS: true,
    TECHWORLD: true
  }
};

// In-memory log storage for debugging
const logStore = [];

// Current correlation ID for request tracing
let currentCorrelationId = null;

/**
 * Generates a unique correlation ID for request tracing
 * @returns {string} Unique correlation ID
 */
const generateCorrelationId = () => {
  return `ts-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Formats a log entry for console output
 * @param {Object} entry - Log entry to format
 * @returns {string} Formatted console output
 */
const formatConsoleOutput = (entry) => {
  const levelIcons = {
    TRACE: '[TRACE]',
    DEBUG: '[DEBUG]',
    INFO: '[INFO]',
    WARN: '[WARN]',
    ERROR: '[ERROR]',
    FATAL: '[FATAL]'
  };

  const prefix = levelIcons[entry.level] || '[LOG]';
  const contextLabel = entry.context ? `[${entry.context}]` : '';
  const correlationLabel = entry.correlationId ? `(${entry.correlationId.slice(-8)})` : '';
  const timestamp = config.enableTimestamps ? `${entry.timestamp} ` : '';

  return `${prefix} ${timestamp}${contextLabel}${correlationLabel} ${entry.message}`;
};

/**
 * Core logging function
 * @param {string} level - Log level (TRACE, DEBUG, INFO, WARN, ERROR, FATAL)
 * @param {string} context - Context/module name
 * @param {string} message - Log message
 * @param {Object|null} data - Additional data to log
 * @param {Error|null} error - Error object if applicable
 * @returns {Object|undefined} Log entry if logged
 */
const log = (level, context, message, data = null, error = null) => {
  // Check if logging is enabled for this level and context
  if (LOG_LEVELS[level] < config.level) return;
  if (context && !config.contexts[context]) return;

  const entry = {
    id: generateCorrelationId(),
    timestamp: new Date().toISOString(),
    level,
    context,
    message,
    data: data ? JSON.parse(JSON.stringify(data)) : null,
    error: error ? {
      message: error.message,
      stack: error.stack,
      name: error.name,
      code: error.code
    } : null,
    correlationId: currentCorrelationId,
    url: typeof window !== 'undefined' ? window.location.pathname : null
  };

  // Store in memory
  if (config.enableStorage) {
    logStore.push(entry);
    if (logStore.length > config.maxStoredLogs) {
      logStore.shift();
    }
  }

  // Console output
  if (config.enableConsole) {
    const consoleOutput = formatConsoleOutput(entry);

    switch (level) {
      case 'TRACE':
      case 'DEBUG':
        console.debug(consoleOutput, data || '');
        break;
      case 'INFO':
        console.info(consoleOutput, data || '');
        break;
      case 'WARN':
        console.warn(consoleOutput, data || '');
        break;
      case 'ERROR':
      case 'FATAL':
        console.error(consoleOutput, data || '', error || '');
        break;
      default:
        console.log(consoleOutput, data || '');
    }
  }

  return entry;
};

/**
 * Creates a context-specific logger with standard log methods
 * @param {string} contextName - Name of the logging context
 * @returns {Object} Logger object with trace, debug, info, warn, error methods
 */
const createContextLogger = (contextName) => ({
  trace: (msg, data) => log('TRACE', contextName, msg, data),
  debug: (msg, data) => log('DEBUG', contextName, msg, data),
  info: (msg, data) => log('INFO', contextName, msg, data),
  warn: (msg, data) => log('WARN', contextName, msg, data),
  error: (msg, data, err) => log('ERROR', contextName, msg, data, err),
  fatal: (msg, data, err) => log('FATAL', contextName, msg, data, err)
});

/**
 * Universal Application Logger
 * Provides structured logging with contexts, correlation IDs, and log storage
 */
const logger = {
  /**
   * Configure the logger
   * @param {Object} newConfig - Configuration options to merge
   */
  configure: (newConfig) => {
    Object.assign(config, newConfig);
  },

  /**
   * Set the minimum log level
   * @param {string} level - Log level name (TRACE, DEBUG, INFO, WARN, ERROR, FATAL, NONE)
   */
  setLevel: (level) => {
    if (LOG_LEVELS[level] !== undefined) {
      config.level = LOG_LEVELS[level];
    }
  },

  /**
   * Enable or disable a logging context
   * @param {string} context - Context name
   * @param {boolean} enabled - Whether to enable the context
   */
  enableContext: (context, enabled = true) => {
    config.contexts[context] = enabled;
  },

  /**
   * Start a new correlation trace
   * @param {string} label - Label for the trace
   * @returns {string} Correlation ID
   */
  startTrace: (label = 'operation') => {
    currentCorrelationId = generateCorrelationId();
    log('INFO', 'TRACE', `Starting trace: ${label}`, { correlationId: currentCorrelationId });
    return currentCorrelationId;
  },

  /**
   * End the current correlation trace
   * @param {Object} summary - Summary data for the trace
   */
  endTrace: (summary = {}) => {
    if (currentCorrelationId) {
      log('INFO', 'TRACE', 'Ending trace', { correlationId: currentCorrelationId, ...summary });
      currentCorrelationId = null;
    }
  },

  /**
   * Get the current correlation ID
   * @returns {string|null} Current correlation ID
   */
  getCorrelationId: () => currentCorrelationId,

  /**
   * Set a specific correlation ID (useful for API response tracing)
   * @param {string} id - Correlation ID to set
   */
  setCorrelationId: (id) => {
    currentCorrelationId = id;
  },

  // ============ Context-Specific Loggers ============

  /**
   * API request/response logging
   */
  api: {
    ...createContextLogger('API'),

    /**
     * Log an API request
     * @param {string} method - HTTP method
     * @param {string} endpoint - API endpoint
     * @param {Object} params - Request parameters
     */
    request: (method, endpoint, params = {}) => {
      log('INFO', 'API', `${method} ${endpoint}`, {
        method,
        endpoint,
        params,
        hasToken: !!params.token
      });
    },

    /**
     * Log an API response
     * @param {string} method - HTTP method
     * @param {string} endpoint - API endpoint
     * @param {number} status - HTTP status code
     * @param {number} duration - Request duration in ms
     * @param {Object} data - Response data summary
     */
    response: (method, endpoint, status, duration, data = {}) => {
      const level = status >= 400 ? 'ERROR' : 'INFO';
      log(level, 'API', `${method} ${endpoint} - ${status}`, {
        method,
        endpoint,
        status,
        duration,
        dataSize: JSON.stringify(data).length
      });
    },

    /**
     * Log an API error
     * @param {string} method - HTTP method
     * @param {string} endpoint - API endpoint
     * @param {Error} error - Error object
     */
    failed: (method, endpoint, error) => {
      log('ERROR', 'API', `${method} ${endpoint} failed`, {
        method,
        endpoint,
        status: error.status,
        code: error.code
      }, error);
    }
  },

  /**
   * Authentication event logging
   */
  auth: {
    ...createContextLogger('AUTH'),

    /**
     * Log a sign-in event
     * @param {string} method - Sign-in method (email, google, etc.)
     * @param {boolean} success - Whether sign-in was successful
     * @param {string} email - User email (optional)
     */
    signIn: (method, success, email = null) => {
      const level = success ? 'INFO' : 'WARN';
      log(level, 'AUTH', `Sign-in ${success ? 'successful' : 'failed'}`, {
        method,
        success,
        email: email ? `${email.substring(0, 3)}***` : null
      });
    },

    /**
     * Log a sign-out event
     */
    signOut: () => {
      log('INFO', 'AUTH', 'User signed out');
    },

    /**
     * Log a session event
     * @param {string} event - Event type (SIGNED_IN, TOKEN_REFRESHED, etc.)
     * @param {boolean} hasSession - Whether session exists
     */
    sessionEvent: (event, hasSession) => {
      log('INFO', 'AUTH', `Auth event: ${event}`, { hasSession });
    },

    /**
     * Log a token refresh
     * @param {boolean} success - Whether refresh was successful
     */
    tokenRefresh: (success) => {
      const level = success ? 'DEBUG' : 'WARN';
      log(level, 'AUTH', `Token refresh ${success ? 'successful' : 'failed'}`, { success });
    }
  },

  /**
   * React component lifecycle logging
   */
  component: {
    ...createContextLogger('COMPONENT'),

    /**
     * Log component mount
     * @param {string} name - Component name
     * @param {Object} props - Component props (sanitized)
     */
    mounted: (name, props = {}) => {
      log('DEBUG', 'COMPONENT', `${name} mounted`, {
        props: Object.keys(props)
      });
    },

    /**
     * Log component unmount
     * @param {string} name - Component name
     */
    unmounted: (name) => {
      log('DEBUG', 'COMPONENT', `${name} unmounted`);
    },

    /**
     * Log component render
     * @param {string} name - Component name
     * @param {string} reason - Render reason
     */
    rendered: (name, reason = 'update') => {
      log('TRACE', 'COMPONENT', `${name} rendered`, { reason });
    },

    /**
     * Log component error
     * @param {string} name - Component name
     * @param {Error} error - Error object
     */
    error: (name, error) => {
      log('ERROR', 'COMPONENT', `${name} error`, { component: name }, error);
    }
  },

  /**
   * State change logging
   */
  state: {
    ...createContextLogger('STATE'),

    /**
     * Log a state change
     * @param {string} source - State source (context, hook, component)
     * @param {string} field - Field that changed
     * @param {*} value - New value (will be summarized)
     */
    changed: (source, field, value) => {
      let summary;
      if (Array.isArray(value)) {
        summary = `Array(${value.length})`;
      } else if (typeof value === 'object' && value !== null) {
        summary = `Object(${Object.keys(value).length} keys)`;
      } else {
        summary = typeof value === 'string' && value.length > 50
          ? `${value.substring(0, 50)}...`
          : value;
      }
      log('DEBUG', 'STATE', `${source}.${field} changed`, { field, value: summary });
    },

    /**
     * Log state initialization
     * @param {string} source - State source
     * @param {Object} initialState - Initial state (keys only)
     */
    initialized: (source, initialState) => {
      log('INFO', 'STATE', `${source} initialized`, {
        fields: Object.keys(initialState)
      });
    }
  },

  /**
   * User interaction logging
   */
  ui: {
    ...createContextLogger('UI'),

    /**
     * Log a user click
     * @param {string} element - Element identifier
     * @param {Object} data - Additional click data
     */
    clicked: (element, data = {}) => {
      log('INFO', 'UI', `Clicked: ${element}`, data);
    },

    /**
     * Log navigation
     * @param {string} from - Previous route
     * @param {string} to - New route
     */
    navigated: (from, to) => {
      log('INFO', 'UI', `Navigated: ${from} -> ${to}`, { from, to });
    },

    /**
     * Log form submission
     * @param {string} formName - Form identifier
     * @param {boolean} valid - Whether form is valid
     */
    formSubmitted: (formName, valid) => {
      log('INFO', 'UI', `Form submitted: ${formName}`, { valid });
    },

    /**
     * Log modal open/close
     * @param {string} modalName - Modal identifier
     * @param {boolean} open - Whether modal is opening
     */
    modal: (modalName, open) => {
      log('DEBUG', 'UI', `Modal ${open ? 'opened' : 'closed'}: ${modalName}`);
    }
  },

  /**
   * Error logging with enhanced context
   */
  error: {
    ...createContextLogger('ERROR'),

    /**
     * Log an unhandled error
     * @param {Error} error - Error object
     * @param {string} source - Error source
     */
    unhandled: (error, source = 'unknown') => {
      log('FATAL', 'ERROR', `Unhandled error in ${source}`, { source }, error);
    },

    /**
     * Log a caught error
     * @param {Error} error - Error object
     * @param {string} handler - Handler that caught the error
     * @param {boolean} recovered - Whether the app recovered
     */
    caught: (error, handler, recovered = true) => {
      const level = recovered ? 'WARN' : 'ERROR';
      log(level, 'ERROR', `Error caught in ${handler}`, {
        handler,
        recovered
      }, error);
    },

    /**
     * Log a validation error
     * @param {string} field - Field with error
     * @param {string} message - Error message
     */
    validation: (field, message) => {
      log('WARN', 'ERROR', `Validation error: ${field}`, { field, message });
    }
  },

  /**
   * Performance timing logging
   */
  perf: {
    ...createContextLogger('PERF'),

    /**
     * Start a performance timer
     * @param {string} label - Timer label
     * @returns {Object} Timer object with end() method
     */
    time: (label) => {
      const startTime = performance.now();
      return {
        end: (additionalData = {}) => {
          const duration = Math.round(performance.now() - startTime);
          log('INFO', 'PERF', `${label}: ${duration}ms`, { duration, ...additionalData });
          return duration;
        }
      };
    },

    /**
     * Log a performance metric
     * @param {string} metric - Metric name
     * @param {number} value - Metric value
     * @param {string} unit - Unit of measurement
     */
    metric: (metric, value, unit = 'ms') => {
      log('INFO', 'PERF', `${metric}: ${value}${unit}`, { metric, value, unit });
    }
  },

  /**
   * Vendor Competitive Analysis page logging.
   * Use logger.vendorAnalysis.info / .warn / .error from the
   * VendorCompetitiveAnalysis components to flow events through the
   * universal log buffer with context: 'VENDOR_ANALYSIS'.
   */
  vendorAnalysis: createContextLogger('VENDOR_ANALYSIS'),

  /**
   * TechWorld feature logging (game-style tech-stack simulation).
   * Use logger.techWorld.info / .warn / .error from any TechWorld component
   * to flow events through the universal log buffer with context: 'TECHWORLD'.
   * See TECHWORLD_NORTH_STAR.md rule E2.
   */
  techWorld: createContextLogger('TECHWORLD'),

  /**
   * Account Lists feature logging — "My Lists" surface and import flow
   * (AccountListsPage, ImportFlowModal, ListsContext). Covers fuzzy matches,
   * embedding rerank, file extraction, member mutations. Context:
   * 'ACCOUNT_LISTS'. See docs/ACCOUNT_LISTS.md.
   */
  accountLists: createContextLogger('ACCOUNT_LISTS'),

  // ============ Log Retrieval and Management ============

  /**
   * Get stored logs with optional filtering
   * @param {Object} options - Filter options
   * @returns {Array} Filtered log entries
   */
  getLogs: (options = {}) => {
    let result = [...logStore];

    if (options.level) {
      const minLevel = LOG_LEVELS[options.level] || 0;
      result = result.filter(e => LOG_LEVELS[e.level] >= minLevel);
    }

    if (options.context) {
      result = result.filter(e => e.context === options.context);
    }

    if (options.correlationId) {
      result = result.filter(e => e.correlationId === options.correlationId);
    }

    if (options.since) {
      const sinceTime = new Date(options.since).getTime();
      result = result.filter(e => new Date(e.timestamp).getTime() >= sinceTime);
    }

    if (options.search) {
      const searchLower = options.search.toLowerCase();
      result = result.filter(e =>
        e.message.toLowerCase().includes(searchLower) ||
        JSON.stringify(e.data || {}).toLowerCase().includes(searchLower)
      );
    }

    if (options.limit) {
      result = result.slice(-options.limit);
    }

    return result;
  },

  /**
   * Export logs as a downloadable JSON file
   */
  exportLogs: () => {
    const blob = new Blob(
      [JSON.stringify(logStore, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `techstack-logs-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Clear all stored logs
   */
  clearLogs: () => {
    logStore.length = 0;
  },

  /**
   * Get log statistics
   * @returns {Object} Log statistics
   */
  getStats: () => {
    const stats = {
      total: logStore.length,
      byLevel: {},
      byContext: {},
      oldest: logStore[0]?.timestamp,
      newest: logStore[logStore.length - 1]?.timestamp
    };

    logStore.forEach(entry => {
      stats.byLevel[entry.level] = (stats.byLevel[entry.level] || 0) + 1;
      stats.byContext[entry.context] = (stats.byContext[entry.context] || 0) + 1;
    });

    return stats;
  }
};

// Make available globally for debugging in console
if (typeof window !== 'undefined') {
  window.AppLogger = logger;
}

export default logger;
export { LOG_LEVELS };
